<template>
  <div
    class="relative flex flex-col w-full min-h-full bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600">
    <!-- Close button positioned absolutely -->
    <button
      type="button"
      class="absolute top-4 right-4 z-10 flex justify-center items-center rounded-full transition-all duration-150 cursor-pointer size-8 bg-white/20 text-white/90 hover:bg-white/30 active:scale-95"
      @click="handleClose">
      <fa-icon :icon="['fas', 'xmark']" />
    </button>

    <!-- Gradient background content -->
    <div class="flex flex-col p-6 pt-14 w-full flex-1">
      <!-- Header with contextual icon -->
      <div class="flex-shrink-0 mb-6 text-center">
        <div
          class="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-white/20 mb-4">
          <fa-icon :icon="['fas', actionIcon]" class="text-2xl text-white" />
        </div>
        <h2 class="mb-2 text-xl font-bold text-white">{{ actionTitle }}</h2>
        <p class="text-white/90 text-sm">{{ actionDescription }}</p>
      </div>

      <!-- Feature highlights -->
      <div class="flex-shrink-0 mb-6 space-y-3">
        <div
          v-for="(benefit, index) in benefits"
          :key="index"
          class="flex items-start space-x-3">
          <div
            class="flex-shrink-0 w-5 h-5 rounded bg-emerald-500 flex items-center justify-center mt-0.5">
            <fa-icon :icon="['fas', 'check']" class="text-xs text-white" />
          </div>
          <div>
            <p class="text-sm font-medium text-white">{{ benefit }}</p>
          </div>
        </div>
      </div>

      <!-- Action buttons -->
      <div class="flex-shrink-0 space-y-3">
        <AuthButton
          :to="{
            name: 'register',
            query: { redirect: redirectPath, action: actionType }
          }"
          variant="gradient"
          size="md"
          :full-width="true"
          :icon="['fas', 'user-plus']"
          text="Create Free Account"
          @click="handleRegisterClick" />

        <AuthButton
          :to="{
            name: 'auth',
            query: { redirect: redirectPath, action: actionType }
          }"
          variant="outline"
          size="md"
          :full-width="true"
          :icon="['fas', 'sign-in-alt']"
          text="I Already Have an Account"
          @click="handleSignInClick" />
      </div>
    </div>

    <!-- Footer with CLAUDE.md compliant button layout -->
    <!-- <div class="p-4 bg-black/20 border-t border-white/20">
      <button
        type="button"
        class="w-full py-3 text-sm font-medium text-white/70 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
        @click="handleClose">
        Maybe Later
      </button>
    </div> -->
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useHapticFeedback } from '@/composables/useHapticFeedback'
import AuthButton from '@/components/Pure/AuthButton.vue'

const props = defineProps({
  actionType: {
    type: String,
    required: true,
    validator: (value) =>
      [
        'review',
        'addToList',
        'markWatched',
        'follow',
        'createList',
        'favorite'
      ].includes(value)
  },
  contextData: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['close', 'register', 'signIn'])

const route = useRoute()
const haptics = useHapticFeedback()

// Contextual content based on action type
const actionContent = computed(() => {
  const content = {
    review: {
      icon: 'star',
      title: 'Write Your First Review',
      description:
        'Share your thoughts and join the conversation with fellow movie lovers.',
      benefits: [
        'Write detailed reviews with ratings',
        'Track your viewing history',
        'Get personalized recommendations'
      ]
    },
    addToList: {
      icon: 'list',
      title: 'Save to Your Lists',
      description:
        'Create and organize your personal movie and TV collections.',
      benefits: [
        'Create unlimited custom lists',
        'Follow lists from other users',
        'Share your collections publicly'
      ]
    },
    markWatched: {
      icon: 'eye',
      title: "Track What You've Watched",
      description:
        "Keep a record of everything you've seen and discover new favourites.",
      benefits: [
        'Automatic watch history tracking',
        'See your viewing statistics',
        'Get tailored recommendations'
      ]
    },
    follow: {
      icon: 'user-plus',
      title: 'Follow Other Users',
      description:
        'Connect with fellow film enthusiasts and discover their favourites.',
      benefits: [
        'See reviews from people you follow',
        'Discover new movies and shows',
        'Build your film community'
      ]
    },
    createList: {
      icon: 'plus',
      title: 'Create Your Own Lists',
      description:
        'Organise your favourite movies and shows into custom collections.',
      benefits: [
        'Unlimited list creation',
        'Share with the community',
        'Collaborative list features'
      ]
    },
    favorite: {
      icon: 'heart',
      title: 'Save Your Favourites',
      description: 'Keep track of the movies and shows you love most.',
      benefits: [
        'Quick access to favourites',
        'Personal recommendation engine',
        'Share your taste with others'
      ]
    }
  }

  return content[props.actionType] || content.review
})

const actionIcon = computed(() => actionContent.value.icon)
const actionTitle = computed(() => actionContent.value.title)
const actionDescription = computed(() => actionContent.value.description)
const benefits = computed(() => actionContent.value.benefits)

const redirectPath = computed(() => {
  return route.fullPath
})

const handleRegisterClick = () => {
  haptics.medium()
  emit('register', {
    actionType: props.actionType,
    contextData: props.contextData
  })
  emit('close')
}

const handleSignInClick = () => {
  haptics.medium()
  emit('signIn', {
    actionType: props.actionType,
    contextData: props.contextData
  })
  emit('close')
}

const handleClose = () => {
  haptics.light()
  emit('close')
}
</script>
